import { Subscription, combineLatest, Observable, fromEventPattern, NEVER, merge } from 'rxjs';
import { Store } from '@ngxs/store';
import {
  map,
  publishReplay,
  refCount,
  filter,
  skipWhile,
  take,
  distinctUntilChanged,
  switchMap,
  pairwise,
  concatMap,
  tap,
  mapTo,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { RouterService } from '@nimbus-library/features/navigation/router.service';
import { startPeriodicAction } from '@nimbus-library/features/shared/store/helpers/start-periodic-action.function';
import { NavigationActions } from '@nimbus-library/features/navigation/navigation.actions';
import { MenuActions } from '@nimbus-library/features/menu/state/menu.actions';
import { TeamState } from '@nimbus-portal/common/store/teams/teams.state';
import { LoadTeams, LoadBot } from '@nimbus-portal/common/store/teams/teams.actions';
import { CacheService } from '@nimbus-library/features/cache/index';
import { FeaturePermissionActions } from '@nimbus-library/features/feature-permission/store/feature-permission.actions';
import { ModalService } from '@nimbus-library/features/modal/services/modal.service';
import { LocalizationActions } from '@nimbus-library/features/localization/store/localization.action';
import { IGeoLocationService } from '@nimbus-global/features/geo-location/services/i-geo-location.service';
import { TranslationObserverService } from '@nimbus-library/features/localization/services/translation-observer.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { ReportingUserSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-user-sessions.service';
import { MySessionsNotificationService } from '@nimbus-portal/features/my-sessions/services/my-sessions-notification.service';
import { PickupContextLinksService } from '@nimbus-portal/common/services/domain/pickup-context-links.service';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { CommonThemeActions } from '@nimbus-library/features/shared/store/theme-store/common-theme.actions';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';
import { ReportingServiceSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-service-sessions.service';
import { UserPreferencesNotificationsActions } from '@nimbus-library/features/user-settings/store/user-preferences-notifications.actions';
import { UserPreferencesNotificationSenderService } from '@nimbus-library/features/user-settings/services/user-preferences-notification-sender/user-preferences-notification-sender.service';
import { UserTasksCapacityService } from '@nimbus-global/features/user-tasks-capacity/services/user-tasks-capacity.service';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { ITeamsContext } from '@nimbus-library/features/microsoft-teams/models/i-teams.context';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { NgxsStateLoggerService } from '@nimbus-library/features/logger/services/state/ngxs-state.logger';
import { AcUserProfileActions } from '@nimbus-portal/features/attendant-console/store/ac-user-profile/ac-user-profile.actions';
import { UiProfilePortalActions } from '@nimbus-portal/features/ui-profile/store/ui-profile-portal.actions';
import { SelectedTeamActions } from '@nimbus-global/features/shared/store/selected-team-state/selected-team.actions';

interface AppRouteData {
  loadContext: boolean;
  loadRole: boolean;
}

@Injectable({ providedIn: 'root' })
export class InitializationService {
  public readonly routeData$: Observable<AppRouteData>;
  public readonly isAuthenticated$: Observable<boolean>;
  public readonly appState$: Observable<[boolean, AppRouteData]>;
  public readonly context$: Observable<ITeamsContext>;

  public constructor(
    private readonly store: Store,
    private readonly stateLogger: NgxsStateLoggerService,
    private readonly logger: ILogger,
    private readonly routerService: RouterService,
    private readonly authenticationService: IAuthenticationService,
    private readonly router: Router,
    private readonly modalService: ModalService,
    private readonly teamsService: IMicrosoftTeamsContextAccessor,
    private readonly geoLocationService: IGeoLocationService,
    private readonly cacheService: CacheService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly reportingServiceSessionsService: ReportingServiceSessionsService,
    private readonly reportingUserSessionsService: ReportingUserSessionsService,
    private readonly mySessionsNotificationService: MySessionsNotificationService,
    private readonly pickupContextLinksService: PickupContextLinksService,
    private readonly translateObserverService: TranslationObserverService,
    private readonly userPreferencesNotificationSenderService: UserPreferencesNotificationSenderService,
    private readonly userTasksCapacityService: UserTasksCapacityService,
  ) {
    this.routeData$ = routerService.getData$<AppRouteData>().pipe(
      map((data) => ({
        loadContext: data.loadContext === undefined ? true : data.loadContext,
        loadRole: data.loadRole === undefined ? true : data.loadRole,
      })),
      publishReplay(1),
      refCount(),
    );

    this.isAuthenticated$ = this.authenticationService.isAuthenticated$.pipe(skipWhile((isAuthenticated) => isAuthenticated === undefined));

    this.appState$ = combineLatest([this.isAuthenticated$, this.routeData$]).pipe(publishReplay(1), refCount());

    this.context$ = this.store.select(TeamState.context).pipe(skipWhile((context) => !context));
  }

  public async initialize(): Promise<Subscription> {
    this.permissionManagerService.initialize();
    this.reportingServiceSessionsService.initialize();
    this.reportingUserSessionsService.initialize();
    this.userTasksCapacityService.initialize();
    this.mySessionsNotificationService.initialize();
    this.pickupContextLinksService.initialize();
    this.userPreferencesNotificationSenderService.initialize();
    this.geoLocationService.resolveSelectedLocation();

    this.store.dispatch(new LoadTeams());
    this.store.dispatch(new MenuActions.Initialize());
    this.store.dispatch(new LocalizationActions.LoadLanguage());
    this.store.dispatch(new LocalizationActions.LoadDateFormat());
    this.store.dispatch(new UserPreferencesNotificationsActions.Initialize());

    const subscription$ = new Subscription();
    subscription$.add(
      combineLatest([this.appState$, this.context$])
        .pipe(
          filter(([[isAuthenticated, options], context]) => options.loadContext),
          take(1),
        )
        .subscribe(([[isAuthenticated, options], context]) => {
          if (context.groupId && isAuthenticated) {
            this.store.dispatch(new LoadBot());
          }
        }),
    );
    subscription$.add(
      combineLatest([this.appState$, this.context$])
        .pipe(
          filter(([[isAuthenticated, options], context]) => options.loadContext && context.userObjectId !== undefined),
          take(1),
        )
        .subscribe(([[isAuthenticated, options], context]) => {
          if (context.subEntityId) {
            // Maybe need to find better place later
            return this.router.navigateByUrl(`/${context.subEntityId}`);
          }
        }),
    );
    subscription$.add(
      startPeriodicAction(
        this.store,
        new NavigationActions.StartTrackNavigation(),
        new NavigationActions.StopTrackNavigation(),
      ).subscribe(),
    );
    subscription$.add(this.startTrackAuthentication());
    subscription$.add(this.startTrackThemeChanges());
    subscription$.add(this.startListenLogging());
    subscription$.add(this.startManagePersistable());
    subscription$.add(this.startTrackApiPermissions());
    subscription$.add(this.startTrackLocation());
    subscription$.add(this.startSyncSelectedTeamWithUrl());
    subscription$.add(this.translateObserverService.startUpdateSequence().subscribe());
    subscription$.add(new Subscription(() => this.modalService.ngOnDestroy()));
    subscription$.add(new Subscription(() => this.translateObserverService.ngOnDestroy()));
    return subscription$;
  }

  private startTrackLocation(): Subscription {
    return this.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter((isAuthenticated) => !!isAuthenticated),
        concatMap((val) =>
          merge(this.routerService.navigationEnd$().pipe(mapTo(false)), this.geoLocationService.triggerLocationResolver$).pipe(take(1)),
        ),
        tap((locationSelected) => {
          const path = window.location.pathname;
          // 1. Login - returnUrl - provisioning/new - query
          // 2. path provisioning/new
          this.geoLocationService.resolveLocation(
            path === '/authentication/login' ? this.router.routerState.snapshot.root.queryParams['returnUrl'] : path,
            locationSelected,
          );
        }),
      )
      .subscribe();
  }

  private startSyncSelectedTeamWithUrl(): Subscription {
    return this.routerService
      .navigationEnd$()
      .pipe(
        map(() => this.router.routerState.snapshot.root.queryParamMap.get('groupId') || null),
        distinctUntilChanged(),
      )
      .subscribe((groupId) => {
        if (groupId) {
          this.store.dispatch(new SelectedTeamActions.SetTeam(this.teamsService.snapshot));
        } else if (!this.teamsService.snapshot?.groupId) {
          this.store.dispatch(new SelectedTeamActions.SetTeam(null));
        }
      });
  }

  private startTrackAuthentication(): Subscription {
    return this.isAuthenticated$.pipe(distinctUntilChanged()).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        const userInfo = this.authenticationService.getUserInfo();
        this.logger.setUser(userInfo.userId, userInfo.userName);
      } else {
        this.logger.resetUser();
      }
    });
  }

  private startTrackApiPermissions(): Subscription {
    // `isAuthenticated$` re-emits on every token refresh / auth state touch. Without
    // distinctUntilChanged + filter(Boolean) each refresh would re-dispatch the three
    // initialisation actions (feature permissions, license info, UI profile) — multiplying both
    // HTTP load and the feature-flag check on the UI profile path. Only fire on transitions
    // into the authenticated state.
    return this.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter((isAuthenticated) => !!isAuthenticated),
      )
      .subscribe(() => {
        this.store.dispatch(new FeaturePermissionActions.Initialize());
        this.store.dispatch(new AcUserProfileActions.LoadUserLicenseInfo());
        this.store.dispatch(new UiProfilePortalActions.LoadUserUiProfile());
      });
  }

  private startTrackThemeChanges(): Subscription {
    return this.teamsService
      .getClientInfo$()
      .pipe(
        take(1),
        switchMap((x) =>
          x !== ClientType.Browser
            ? this.teamsService.getThemeInformation$().pipe(
                map((theme) => {
                  switch (theme) {
                    case 'dark':
                      return ChartTheme.Dark;
                    case 'contrast':
                      return ChartTheme.HighContrast;
                    default:
                      return ChartTheme.Default;
                  }
                }),
              )
            : this.store.dispatch(new CommonThemeActions.Initialize()).pipe(switchMap(() => this.store.select(CommonThemeState.theme))),
        ),
        distinctUntilChanged(),
      )
      .subscribe((theme) => {
        this.store.dispatch(new CommonThemeActions.SetTheme(theme));
      });
  }

  private startListenLogging(): Subscription {
    return this.isAuthenticated$
      .pipe(
        switchMap((isAuthenticated) => {
          if (isAuthenticated) {
            return fromEventPattern(
              () => this.stateLogger.startListening(),
              () => this.stateLogger.stopListening(),
            );
          }
          return NEVER;
        }),
      )
      .subscribe();
  }

  private startManagePersistable(): Subscription {
    return this.authenticationService.isAuthenticated$
      .pipe(
        skipWhile((isAuthenticated) => !isAuthenticated),
        distinctUntilChanged(),
        pairwise(),
        map(([, isAuthenticated2]) => isAuthenticated2),
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.cacheService.persist();
        } else {
          this.cacheService.unPersist();
        }
      });
  }
}
